import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Grid, MenuItem } from '@mui/material';
import { Form, Formik } from 'formik';
import toast, { Toaster } from 'react-hot-toast';
import { colors } from '../../assets/utils/colors';
import CustomCard2 from '../../components/cards/CustomCard2';
import CustomeFileForm from '../../components/forms/CustomeFileForm';
import CustomeMultlineForm from '../../components/forms/CustomeMultlineForm';
import CustomTextField from '../../components/forms/CustomTextField';
import ProductsSidebar from '../../components/sidebars/ProductsSidebar';
import PageLayout from '../layouts/PageLayout';
import * as Yup from 'yup';
import { Image } from '@mui/icons-material';
import CustomSelectForm from '../../components/forms/CustomSelectForm';
import { useSelector } from 'react-redux';
import { filter, map, size } from 'lodash';
import { useAddNewProductMutation } from '../../api/products/productAPI';
import {
    branchesSelector,
    brandsSelector,
    categoriesSelector,
} from '../../features/selectors';
import CustomSubmitButton from '../../components/forms/CustomSubmitButton';

// ################### validation schema ###############
const createProductSchema = Yup.object({
    brand_id: Yup.string().required('Brand is required'),
    category_id: Yup.string().required('Category is required'),
    branch_id: Yup.string().required('Branch is required'),
    sub_category_id: Yup.string().required('Sub category is required'),
    product_name: Yup.string().required('Name is required'),
    product_description: Yup.string().required('Description is required'),
    file: Yup.mixed().required('Product image is required'),
    product_weight: Yup.string().required('Weight is required'),
    product_sku: Yup.string().required('SKU is required'),
    product_quantity: Yup.number().required('Product quantity is required'),
    expire_date: Yup.date().min(new Date()).required('Expire date is required'),
    product_price: Yup.string().required('Price is required'),
    isOnSale: Yup.boolean().required('Identfy if product is onsell'),
    discounted_percent: Yup.string().max(
        2,
        'Discounted percent must be at most 2 characters',
    ),
});

// !################# MAIN FUNC ##################
const CreateProduct = () => {
    // ############# CONST ###############
    const navigate = useNavigate();

    // ############ Redux state ################
    const { brands } = useSelector(brandsSelector);
    const { branches } = useSelector(branchesSelector);
    const { categories, sub_categories } = useSelector(categoriesSelector);

    const [
        addProduct,
        { isSuccess, isLoading, isError, data: product },
    ] = useAddNewProductMutation();

    // ################# FUNC ###################
    // -> handle file preview
    const [filePreview, setFilePreview] = useState('');
    // => Get sub-categories
    const getSubCategories = (id) => {
        return filter(sub_categories, ({ category_id }) => category_id === id);
    };

    // ############### UseEffect ##################
    useEffect(() => {
        if (isSuccess) {
            toast.success('Product added successfully');
            navigate(
                `/products/details/${product?.data?.product_details?.product_id}`,
            );
        }
        if (isError) {
            toast.error('Fail to create product. Please try again later');
        }
        // if (isLoading) {
        //     toast.loading('Saving product ... ');
        // }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess, isError]);

    return (
        <>
            <PageLayout sidebar={<ProductsSidebar />} head="Create new product">
                <CustomCard2 title={'Product details'} heightClear={640}>
                    <Formik
                        enableReinitialize
                        initialValues={{
                            brand_id: '',
                            branch_id: '',
                            category_id: '',
                            sub_category_id: '',
                            product_name: '',
                            product_description: '',
                            file: '',
                            product_weight: '',
                            product_sku: '',
                            product_quantity: '',
                            expire_date: '',
                            product_price: '',
                            isOnSale: 0,
                            discounted_percent: 0,
                        }}
                        validationSchema={createProductSchema}
                        onSubmit={async (values) => {
                            const formData = new FormData();

                            formData.append('product_banner', values.file);
                            formData.append('brand_id', values.brand_id);
                            formData.append('branch_id', values.branch_id);
                            formData.append('category_id', values.category_id);
                            formData.append(
                                'sub_category_id',
                                values.sub_category_id,
                            );
                            formData.append(
                                'product_name',
                                values.product_name,
                            );
                            formData.append(
                                'product_description',
                                values.product_description,
                            );
                            formData.append(
                                'product_weight',
                                values.product_weight,
                            );
                            formData.append('product_sku', values.product_sku);
                            formData.append(
                                'product_quantity',
                                values.product_quantity,
                            );
                            formData.append('expire_date', values.expire_date);
                            formData.append(
                                'product_price',
                                values.product_price,
                            );
                            formData.append('isOnSale', values.isOnSale);
                            formData.append(
                                'discounted_percent',
                                values.discounted_percent,
                            );
                            await addProduct(formData);
                        }}
                    >
                        {(formik) => (
                            <Form>
                                <Grid container padding={2} spacing={2}>
                                    <Grid item sm={8}>
                                        <Grid container spacing={2}>
                                            <Grid item sm={6}>
                                                <CustomTextField
                                                    name="product_name"
                                                    label={'Product name'}
                                                    type="text"
                                                    color="primary"
                                                />
                                            </Grid>
                                            <Grid item sm={6}>
                                                <CustomTextField
                                                    name="product_sku"
                                                    label={'Product SKU'}
                                                    type="text"
                                                    color="primary"
                                                />
                                            </Grid>
                                            {/* ############ | brand | category | subcategory| ############# */}
                                            <Grid item sm={4}>
                                                <CustomSelectForm
                                                    name="branch_id"
                                                    formikName="branch_id"
                                                    label="Branch"
                                                    formik={formik}
                                                >
                                                    {size(branches) > 0 ? (
                                                        map(
                                                            branches,
                                                            ({
                                                                branch_name,
                                                                id,
                                                            }) => (
                                                                <MenuItem
                                                                    key={id}
                                                                    value={id}
                                                                >
                                                                    {
                                                                        branch_name
                                                                    }{' '}
                                                                </MenuItem>
                                                            ),
                                                        )
                                                    ) : (
                                                        <MenuItem value={null}>
                                                            No category
                                                        </MenuItem>
                                                    )}
                                                </CustomSelectForm>
                                            </Grid>
                                            <Grid item sm={4}>
                                                <CustomSelectForm
                                                    name="brand_id"
                                                    formikName="brand_id"
                                                    label="Brand"
                                                    formik={formik}
                                                >
                                                    {size(brands) > 0 ? (
                                                        map(
                                                            brands,
                                                            ({
                                                                brand_name,
                                                                id,
                                                            }) => (
                                                                <MenuItem
                                                                    key={id}
                                                                    value={id}
                                                                >
                                                                    {brand_name}{' '}
                                                                </MenuItem>
                                                            ),
                                                        )
                                                    ) : (
                                                        <MenuItem value={null}>
                                                            No category
                                                        </MenuItem>
                                                    )}
                                                </CustomSelectForm>
                                            </Grid>
                                            <Grid item sm={4}>
                                                <CustomSelectForm
                                                    name="category_id"
                                                    formikName="category_id"
                                                    label="Category"
                                                    formik={formik}
                                                >
                                                    {size(categories) > 0 ? (
                                                        map(
                                                            categories,
                                                            ({
                                                                category_name,
                                                                id,
                                                            }) => (
                                                                <MenuItem
                                                                    key={id}
                                                                    value={id}
                                                                >
                                                                    {
                                                                        category_name
                                                                    }
                                                                </MenuItem>
                                                            ),
                                                        )
                                                    ) : (
                                                        <MenuItem value={null}>
                                                            No category
                                                        </MenuItem>
                                                    )}
                                                </CustomSelectForm>
                                            </Grid>
                                            <Grid item sm={4}>
                                                {/* {getSubCategories(formik.values.category_id)} */}
                                                <CustomSelectForm
                                                    name="sub_category_id"
                                                    formikName="sub_category_id"
                                                    label="Sub category"
                                                    formik={formik}
                                                >
                                                    {size(
                                                        getSubCategories(
                                                            formik.values
                                                                .category_id,
                                                        ),
                                                    ) > 0 ? (
                                                        map(
                                                            getSubCategories(
                                                                formik.values
                                                                    .category_id,
                                                            ),
                                                            ({
                                                                id,
                                                                sub_category_name,
                                                            }) => (
                                                                <MenuItem
                                                                    value={id}
                                                                >
                                                                    {
                                                                        sub_category_name
                                                                    }
                                                                </MenuItem>
                                                            ),
                                                        )
                                                    ) : (
                                                        <MenuItem value={null}>
                                                            No category
                                                        </MenuItem>
                                                    )}
                                                </CustomSelectForm>
                                            </Grid>
                                            {/* ############ |weight | price | discount | ############# */}
                                            <Grid item sm={4}>
                                                <CustomTextField
                                                    name="product_weight"
                                                    label={'Product weight'}
                                                    type="text"
                                                    color="primary"
                                                />
                                            </Grid>
                                            <Grid item sm={4}>
                                                <CustomTextField
                                                    name="product_price"
                                                    label={'Product price'}
                                                    type="text"
                                                    color="primary"
                                                />
                                            </Grid>
                                            <Grid item sm={4}>
                                                <CustomTextField
                                                    name="discounted_percent"
                                                    label={'Discount %'}
                                                    type="number"
                                                    color="primary"
                                                />
                                            </Grid>
                                            {/* ############ |quantity | Expire date | ############# */}
                                            <Grid item sm={4}>
                                                <CustomTextField
                                                    name="product_quantity"
                                                    label={'Product quantity'}
                                                    type="number"
                                                    color="primary"
                                                />
                                            </Grid>
                                            <Grid item sm={4}>
                                                <CustomTextField
                                                    name={'expire_date'}
                                                    label={'Expire date'}
                                                    type="date"
                                                    color="primary"
                                                    shrink
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <Grid container>
                                            <Grid item sm={12} sx={{ mt: 2 }}>
                                                <Box
                                                    sx={{
                                                        height: 225,
                                                        display: 'flex',
                                                        justifyContent:
                                                            'center',
                                                        alignItems: 'center',
                                                        borderRadius: 5,
                                                        background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor1})`,
                                                    }}
                                                >
                                                    {filePreview ? (
                                                        <>
                                                            <img
                                                                src={
                                                                    filePreview
                                                                }
                                                                alt="img"
                                                                style={{
                                                                    width:
                                                                        '100%',
                                                                    height:
                                                                        '10em',
                                                                    objectFit:
                                                                        'contain',
                                                                }}
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Image
                                                                sx={{
                                                                    width:
                                                                        '100%',
                                                                    height:
                                                                        '7em',
                                                                    objectFit:
                                                                        'contain',
                                                                    opacity: 0.7,
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                </Box>
                                            </Grid>
                                            <Grid item sm={12}>
                                                <CustomeFileForm
                                                    name="product_banner"
                                                    label={'Product image'}
                                                    type="file"
                                                    color="primary"
                                                    setFilePreview={
                                                        setFilePreview
                                                    }
                                                    formik={formik}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item sm={8}>
                                        <CustomeMultlineForm
                                            name="product_description"
                                            label={'Product description'}
                                            type="text"
                                            color="primary"
                                            rows={6}
                                        />
                                    </Grid>
                                    <Grid item sm={4}>
                                        <Box
                                            sx={{
                                                height: 150,
                                                mt: 2,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <CustomSubmitButton
                                                loading={isLoading}
                                                onClick={formik.handleSubmit}
                                            >
                                                Create product
                                            </CustomSubmitButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                    <Toaster
                        toastOptions={{
                            duration: 2500,
                        }}
                    />
                </CustomCard2>
            </PageLayout>
        </>
    );
};

export default CreateProduct;
